import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatInt, formatPercent, ratio } from "@/lib/statsFormat";
import type {
  CallStatsChannelReasonRow,
  CallStatsChannelRow,
  CallStatsHangupCauseRow,
} from "@/services/callStatsService";

const RING_REASONS = [
  "customer-ended-call-during-ring",
  "customer-ended-during-ring",
];

interface PickupStatProps {
  channels: CallStatsChannelRow[];
  reasons: CallStatsChannelReasonRow[];
  causes?: CallStatsHangupCauseRow[];
}

const TOP_CAUSES = 5;

function causeName(cause: string): string {
  return cause ? cause.replace(/_/g, " ") : "Not recorded";
}

// Telephone calls only: a web session has no ringing phase.
const PickupStat = ({ channels, reasons, causes }: PickupStatProps) => {
  const notConnected = (causes ?? [])
    .map((row) => ({ name: causeName(row.cause), count: row.calls - row.connected }))
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);
  const notPickedTotal = notConnected.reduce((acc, row) => acc + row.count, 0);
  const notPicked = notConnected.slice(0, TOP_CAUSES);
  const otherCount = notConnected
    .slice(TOP_CAUSES)
    .reduce((acc, row) => acc + row.count, 0);
  if (otherCount > 0) notPicked.push({ name: "Other", count: otherCount });

  const pickups = channels.find((row) => row.channel === "phone")?.connected ?? 0;
  const endedDuringRing = reasons
    .filter((row) => row.channel === "phone" && RING_REASONS.includes(row.reason))
    .reduce((acc, row) => acc + row.calls, 0);
  const pickupShare = ratio(pickups, pickups + endedDuringRing);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pickups vs ended during ring</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-2xl font-bold">{formatInt(pickups)}</p>
            <p className="text-xs text-muted-foreground">
              picked up · {formatPercent(pickupShare)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{formatInt(endedDuringRing)}</p>
            <p className="text-xs text-muted-foreground">
              customer ended during ring
            </p>
          </div>
        </div>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
          <div
            style={{ width: `${pickupShare ?? 0}%`, background: "var(--chart-1)" }}
          />
          <div
            style={{
              width: pickupShare == null ? 0 : `${100 - pickupShare}%`,
              background: "var(--chart-3)",
            }}
          />
        </div>
        <p className="text-xs text-muted-foreground">Telephone calls only</p>
        {notPicked.length > 0 && (
          <div className="space-y-2 border-t pt-3">
            <h3 className="text-sm font-semibold">Why calls were not picked up</h3>
            <ul className="space-y-1.5 text-sm">
              {notPicked.map((row) => (
                <li key={row.name} className="flex items-center justify-between gap-3">
                  <span className="truncate">{row.name}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatInt(row.count)} ·{" "}
                    {formatPercent((row.count / notPickedTotal) * 100)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PickupStat;
